import assertNever from "assert-never";
import { DayOfWeek, Instant } from "@js-joda/core";
import * as React from "react";
// import * as Ons from "react-onsenui";
import { API, ServerError } from "../API";
import { isDefined } from "../components/Generic/IsDefined";
import { PlanOfCareScreen } from "../components/PlanOfCareScreen";
import { ScreenSkeleton } from "../components/ScreenSkeleton";
import { simpleAlert } from "../components/SimpleAlert";
import { deepEqual } from "../deep-equal";
import { PlanOfCareItemId, VisitInstanceId } from "../messages/ObjectIds";
import { PatientDocumentContentItem } from "../messages/PatientDocuments";
import { PlanOfCare, POCItem, POCItemAnswer, pocItemTypes } from "../messages/PlanOfCare";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { withDefault } from "../utils/withDefault";

export interface PlanOfCareDaysAWeek {
  frequencyPerWeek: number | null;
  visitsPerWeek: number | null;
}
export interface PocChangeParams {
  notes?: string;
  day?: DayOfWeek;
  asRequestedByPatient?: boolean;
  daysAWeek?: PlanOfCareDaysAWeek;
  everyVisit?: boolean;
}
export interface FrequencyVisitedInAWeek {
  visitAWeek: number;
  frequency: number;
}

export interface PlanOfCareItemWithAnswerAndIsOpen {
  item: POCItem;
  answer: POCItemAnswer | null;
  isOpen: boolean;
}

export interface PlanOfCareWithAnswerAndIsOpen {
  poc: PlanOfCare;
  planOfCareType: {
    showPatientSignature: boolean;
  };
  list: PlanOfCareItemWithAnswerAndIsOpen[];
}

export interface InitPlanOfCare {
  initialPlanOfCare: PlanOfCareWithAnswerAndIsOpen;
  initialDocument: PatientDocumentContentItem[];
  initialCaregiverSignature: string | null;
  initialPatientSignature: string | null;
  patientUnsignedReason: string | null;
}

export interface VisitPlanOfCareActivityProps {
  api: API;
  scheduledVisitId: VisitInstanceId;
  onPlanOfCareSubmitted: () => void;
  onBackClick: () => void;
  init: InitPlanOfCare;
}

export interface PlanOfCareActivityState {
  planOfCare: PlanOfCareWithAnswerAndIsOpen;
  dutySheetDocument: PatientDocumentContentItem[];
  caregiverSignature: string | null;
  patientSignature: string | null;
  patientUnsignedReason: string | null;
}

export class VisitPlanOfCareActivity extends React.Component<
  VisitPlanOfCareActivityProps,
  PlanOfCareActivityState
> {
  shouldComponentUpdate(props: VisitPlanOfCareActivityProps, state: PlanOfCareActivityState) {
    return (
      !deepEqual(state.planOfCare, this.state.planOfCare) ||
      !deepEqual(props.init, this.props.init) ||
      props.scheduledVisitId !== this.props.scheduledVisitId ||
      state.caregiverSignature !== this.state.caregiverSignature ||
      state.patientUnsignedReason !== this.state.patientUnsignedReason ||
      state.patientSignature !== this.state.patientSignature
    );
  }
  constructor(props: VisitPlanOfCareActivityProps) {
    super(props);

    this.state = {
      planOfCare: {
        list: this.parseDocument(props.init.initialPlanOfCare.list, props.init.initialDocument),
        poc: props.init.initialPlanOfCare.poc,
        planOfCareType: props.init.initialPlanOfCare.planOfCareType,
      },
      caregiverSignature: props.init.initialCaregiverSignature,
      patientSignature: props.init.initialPatientSignature,
      patientUnsignedReason: props.init.patientUnsignedReason,
      dutySheetDocument: props.init.initialDocument,
    };
  }

  parseDocument(
    list: PlanOfCareItemWithAnswerAndIsOpen[],
    documentToParse: PatientDocumentContentItem[]
  ): PlanOfCareItemWithAnswerAndIsOpen[] {
    const document: PatientDocumentContentItem[] = documentToParse.filter(
      (item) => item.itemType !== pocItemTypes.Section
    );
    const result = list.slice();

    const parseAnswer = (item: PatientDocumentContentItem) => {
      let answer: POCItemAnswer;
      const notes = item.notes === undefined ? null : item.notes;

      if (item.itemType === pocItemTypes.Task) {
        answer = {
          type: "Task",
          notes: notes,
          whenToPerform: { type: "AsRequestedByPatient" },
        };

        // Not Required means its 'as requested by patient'
        if (item.isRequired !== true) {
          return answer;
        }

        answer.whenToPerform = { type: "EveryVisit" };

        const hasItemDays = (item.days?.length ?? 0) > 0;
        const frequency =
          isDefined(item.frequencyPerWeek) && isDefined(item.visitPerWeek)
            ? { frequency: item.frequencyPerWeek, visitsInAWeek: item.visitPerWeek }
            : undefined;

        if (hasItemDays || isDefined(frequency)) {
          answer.whenToPerform = {
            type: "OnDaysOfWeek",
            days: item.days,
            frequency: frequency,
          };
        }
      } else {
        // If item is an attribute
        answer = {
          type: "Attribute",
          notes: notes,
        };
      }

      return answer;
    };

    document.forEach((item) => {
      const foundItem = result.filter(
        (searchItem) => PlanOfCareItemId.unwrap(searchItem.item.id) === item.id
      )[0] as PlanOfCareItemWithAnswerAndIsOpen | undefined;

      if (foundItem !== undefined) {
        // throw new Error("The document contained non-existing plan of care item id: + " + item.id);
        foundItem.answer = parseAnswer(item);
        foundItem.isOpen = true;
      }
    });

    return result;
  }

  getDocument(list: PlanOfCareItemWithAnswerAndIsOpen[]) {
    const sections: any = {};
    let sectionCounter = 9999;

    list
      .filter((item) => item.answer !== null)
      .forEach((item) => {
        let section: any[];
        const answer = item.answer as POCItemAnswer.Task | POCItemAnswer.Attribute;

        if (sections[item.item.section] === undefined) {
          section = [];
          sections[item.item.section] = section;
        } else {
          section = sections[item.item.section];
        }

        if (answer.type === "Task") {
          const task: PatientDocumentContentItem = {
            id: PlanOfCareItemId.unwrap(item.item.id),
            isRequired: answer.whenToPerform.type === "AsRequestedByPatient" ? false : true,
            itemType: "check",
            label: item.item.label,
            mainClass: "multiple",
            parentId: null,
            showIfParentEquals: null,
            possibleAnswers: ["Done"],
            showOtherOption: false,
            notes: answer.notes,
            title: "Multiple Select (check)",
            columns: [
              [
                {
                  id: 1000 + PlanOfCareItemId.unwrap(item.item.id),
                  label: "Notes",
                  title: "Short Text",
                  columns: null,
                  itemType: "textShort",
                  parentId: PlanOfCareItemId.unwrap(item.item.id),
                  mainClass: "single",
                  isRequired: false,
                  parentType: "check",
                  ifParentEquals: "Done",
                  showIfParentEquals: true,
                },
              ],
            ],
          };

          if (answer.whenToPerform.type === "OnDaysOfWeek") {
            task.days = answer.whenToPerform.days;
            task.frequencyPerWeek = withDefault(
              answer.whenToPerform.frequency?.frequency,
              undefined
            );
            task.visitPerWeek = withDefault(
              answer.whenToPerform.frequency?.visitsInAWeek,
              undefined
            );
          }

          section.push(task);
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (answer.type === "Attribute") {
          const attribute: PatientDocumentContentItem = {
            id: PlanOfCareItemId.unwrap(item.item.id),
            itemType: "smallHeader",
            label: item.item.label,
            notes: answer.notes,
            mainClass: "noQuestion",
            parentId: null,
            showIfParentEquals: null,
            title: "Sub Header",
          };

          section.push(attribute);
        }
      });
    const result: PatientDocumentContentItem[] = [];
    Object.keys(sections).forEach((key) => {
      const section = sections[key] as any[];
      result.push(
        {
          id: sectionCounter++,
          itemType: "bigHeader",
          label: key,
          mainClass: "noQuestion",
          title: "Main Header",
          showIfParentEquals: null,
          parentId: null,
        },
        ...section
      );
    });

    return result;
  }

  handleItemToggle = (item: POCItem) => {
    const updatedItemList = this.state.planOfCare.list.map(
      (item1): PlanOfCareItemWithAnswerAndIsOpen => {
        if (item.id === item1.item.id) {
          return {
            item: item1.item,
            answer: item1.isOpen
              ? null
              : item1.answer ?? {
                  type: "Task",
                  whenToPerform: { type: "EveryVisit" },
                  notes: null,
                },
            isOpen: !item1.isOpen,
          };
        }
        return item1;
      }
    );
    this.setState((prevState: PlanOfCareActivityState) => {
      return {
        planOfCare: {
          poc: prevState.planOfCare.poc,
          planOfCareType: prevState.planOfCare.planOfCareType,
          list: updatedItemList,
        },
      };
    });
    this.props.api.setPlanOfCareItemAnswer(
      this.props.scheduledVisitId,
      this.props.init.initialPlanOfCare.poc.id,
      this.getDocument(updatedItemList)
    );
  };

  handleAsRequestedByPatientChange = (item: POCItem) => {
    this.setState((prevState: PlanOfCareActivityState) => {
      const updatedItemList = prevState.planOfCare.list.map(
        (item1): PlanOfCareItemWithAnswerAndIsOpen => {
          if (item.id === item1.item.id) {
            if (item1.answer !== null && item1.answer.type !== "Task") {
              throw new Error("The Impossible Happened. Invalid state.");
            }
            const answer: POCItemAnswer.Task = {
              type: "Task",
              whenToPerform: {
                type: "AsRequestedByPatient",
              },
              notes: item1.answer === null ? null : item1.answer.notes,
            };
            switch (item.type) {
              case "Task": {
                return {
                  item: item1.item,
                  answer: answer,
                  isOpen: item1.isOpen,
                };
              }
              case "Attribute": {
                break;
              }
              case "Predefined":
                break;
              default:
                return assertNever(item.type);
            }
          }
          return item1;
        }
      );
      this.props.api.setPlanOfCareItemAnswer(
        this.props.scheduledVisitId,
        this.props.init.initialPlanOfCare.poc.id,
        this.getDocument(updatedItemList)
      );
      return {
        planOfCare: {
          poc: prevState.planOfCare.poc,
          planOfCareType: prevState.planOfCare.planOfCareType,
          list: updatedItemList,
        },
      };
    });
  };

  handleItemChange = (item: POCItem, val: PocChangeParams) => {
    let countDayRelated = 0;
    if (val.day !== undefined) {
      countDayRelated++;
    }
    if (val.asRequestedByPatient !== undefined) {
      countDayRelated++;
    }
    if (val.everyVisit !== undefined) {
      countDayRelated++;
    }
    if (val.daysAWeek !== undefined) {
      countDayRelated++;
    }
    if (countDayRelated > 1) {
      throw new Error("Can't handle more than one day related parameter");
    }

    const updatedItemList = this.state.planOfCare.list.map((listItem) =>
      updatePlanOfCareListItem(item, val, listItem)
    );
    this.setState((prevState: PlanOfCareActivityState) => {
      return {
        planOfCare: {
          poc: prevState.planOfCare.poc,
          list: updatedItemList,
          planOfCareType: prevState.planOfCare.planOfCareType,
        },
      };
    });
    this.props.api.setPlanOfCareItemAnswer(
      this.props.scheduledVisitId,
      this.props.init.initialPlanOfCare.poc.id,
      this.getDocument(updatedItemList)
    );
  };

  writeSignature = (
    data: string | null,
    type: "Patient" | "Caregiver",
    unsignedReason?: string | null
  ) => {
    this.setState(
      (prevState: PlanOfCareActivityState) => {
        return {
          planOfCare: prevState.planOfCare,
          patientSignature: type === "Patient" ? data : prevState.patientSignature,
          caregiverSignature: type === "Caregiver" ? data : prevState.caregiverSignature,
          patientUnsignedReason:
            unsignedReason !== undefined ? unsignedReason : prevState.patientUnsignedReason,
        };
      },
      () => {
        this.props.api
          .setPlanOfCareSignature(this.state.planOfCare.poc.id, this.props.scheduledVisitId, {
            type: type,
            data: type === "Patient" ? this.state.patientSignature : this.state.caregiverSignature,
            unsignedReason: unsignedReason,
          })
          .catch((e: ServerError) => {
            console.error(e);
            simpleAlert({
              icon: "error",
              title: "Something went wrong",
              message: "Failed to save signature, please try again",
            });
          });
      }
    );
  };

  /**
   * Scroll to the first field that marked as invalid.
   */
  public scrollToFirstInvalidField(): void {
    const element = document.querySelector(".PlanOfCareScreen__requiredEmpty");

    if (element !== null) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  handleSubmitClick = () => {
    const openWithoutPerformingPeriodTasks = this.state.planOfCare.list.filter(
      (item) => item.isOpen && item.item.type === "Task" && item.answer === null
    );
    const isCaregiverSignatureValid = this.state.caregiverSignature !== null;
    const isPatientSignatureValid = this.state.planOfCare.planOfCareType.showPatientSignature
      ? this.state.patientSignature !== null || this.state.patientUnsignedReason !== null
      : true;

    // here get this first missid occure
    if (!isCaregiverSignatureValid && !isPatientSignatureValid) {
      simpleAlert({
        icon: "warning",
        title: "Must sign",
        message: translate(STRINGS.CAREGIVER_SIGNATURE_AND_PATIENT_SIGNATURE_REQUIRE_MSG),
      });
    } else if (!isPatientSignatureValid) {
      simpleAlert({
        icon: "warning",
        title: "Must sign",
        message: translate(STRINGS.PATIENT_SIGNATURE_REQUIRE_MSG),
      });
    } else if (!isCaregiverSignatureValid) {
      simpleAlert({
        icon: "warning",
        title: "Must sign",
        message: translate(STRINGS.CAREGIVER_SIGNATURE_REQUIRE_MSG),
      });
    } else if (openWithoutPerformingPeriodTasks.length > 0) {
      simpleAlert({
        icon: "warning",
        title: "When To Perform Is Missing",
        message: "Please make sure all chosen items have indication on when to perform them",
      });
      this.scrollToFirstInvalidField();

      // here call scroll to
    } else {
      simpleAlert({
        icon: "success",
        title: "Success!",
        message: "Plan of care was saved, don't forget to clock out",
      });
      this.props.api
        .submitPlanOfCare(
          this.props.scheduledVisitId,
          this.props.init.initialPlanOfCare.poc.id,
          this.getDocument(this.state.planOfCare.list)
        )
        .then(() => this.props.onPlanOfCareSubmitted());
      this.props.onBackClick();

      this.setState((prevState: PlanOfCareActivityState) => {
        return {
          planOfCare: {
            poc: {
              patientSignature: prevState.planOfCare.poc.patientSignature,
              patientUnsignedReason: prevState.planOfCare.poc.patientUnsignedReason,
              caregiverSignature: prevState.planOfCare.poc.caregiverSignature,
              id: prevState.planOfCare.poc.id,
              createdAt: prevState.planOfCare.poc.createdAt,
              createdBy: prevState.planOfCare.poc.createdBy,
              submittedAt: Instant.now(),
            },
            planOfCareType: prevState.planOfCare.planOfCareType,
            list: prevState.planOfCare.list,
          },
        };
      });
    }
  };

  render() {
    return (
      <ScreenSkeleton
        title={translate(STRINGS.PLAN_OF_CARE)}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.SECONDARY}
        onBackClick={this.props.onBackClick}
      >
        <PlanOfCareScreen
          planOfCare={this.state.planOfCare}
          caregiverSignature={this.state.caregiverSignature}
          patientSignature={this.state.patientSignature}
          patientUnsignedReason={this.state.patientUnsignedReason}
          onItemChange={this.handleItemChange}
          onItemToggle={this.handleItemToggle}
          onSubmitClick={this.handleSubmitClick}
          onCaregiverSignatureEnd={this.writeSignature}
          onPatientSignatureEnd={this.writeSignature}
        />
      </ScreenSkeleton>
    );
  }
}

export function updatePlanOfCareListItem(
  item: POCItem,
  changeParams: PocChangeParams,
  item1: PlanOfCareItemWithAnswerAndIsOpen
): PlanOfCareItemWithAnswerAndIsOpen {
  if (item.id === item1.item.id) {
    switch (item.type) {
      case "Task": {
        if (item1.answer !== null && item1.answer.type !== "Task") {
          throw new Error("The Impossible Happened. Invalid state.");
        }

        let newWhenToPerform;

        console.log(changeParams);
        if (changeParams.day !== undefined || changeParams.daysAWeek !== undefined) {
          if (item1.answer?.whenToPerform.type === "OnDaysOfWeek") {
            let dayOfWeeks = [...((item1.answer as any)?.whenToPerform?.days ?? [])];
            if (changeParams.day !== undefined) {
              if (dayOfWeeks.indexOf(changeParams.day) === -1) {
                dayOfWeeks.push(changeParams.day);
              } else {
                dayOfWeeks = dayOfWeeks.filter((day) => day !== changeParams.day);
              }
            }

            newWhenToPerform = {
              type: "OnDaysOfWeek" as const,
              days: dayOfWeeks,
              frequency: (item1.answer as any)?.whenToPerform?.frequency,
            };
          } else {
            newWhenToPerform = {
              type: "OnDaysOfWeek" as const,
              days: changeParams.day !== undefined ? [changeParams.day] : ([] as DayOfWeek[]),
            };
          }

          if (changeParams.daysAWeek !== undefined) {
            newWhenToPerform.frequency = {
              frequency: changeParams.daysAWeek.frequencyPerWeek,
              visitsInAWeek: changeParams.daysAWeek.visitsPerWeek,
            };
          }
        } else if (changeParams.asRequestedByPatient === true) {
          newWhenToPerform = {
            type: "AsRequestedByPatient" as const,
          };
        } else if (changeParams.everyVisit === true) {
          newWhenToPerform = {
            type: "EveryVisit" as const,
          };
        }

        const whenToPerform =
          newWhenToPerform !== undefined
            ? newWhenToPerform
            : item1.answer?.whenToPerform ?? { type: "OnDaysOfWeek" as const };

        const notes =
          changeParams.notes !== undefined ? changeParams.notes : item1.answer?.notes ?? null;

        const answer: POCItemAnswer.Task = {
          type: "Task",
          whenToPerform: whenToPerform,
          notes: notes,
        };

        return {
          item: item1.item,
          answer: answer,
          isOpen: item1.isOpen,
        };
      }
      case "Attribute": {
        if (item1.answer !== null && item1.answer.type !== "Attribute") {
          throw new Error("The Impossible Happened. Invalid state.");
        }
        const answer: POCItemAnswer.Attribute = {
          type: "Attribute",
          notes:
            changeParams.notes !== undefined
              ? changeParams.notes
              : item1.answer === null
              ? null
              : item1.answer.notes,
        };
        return {
          item: item1.item,
          answer: answer,
          isOpen: item1.isOpen,
        };
      }
      case "Predefined":
        break;
      default:
        return assertNever(item.type);
    }
  }
  return item1;
}
